import { useLazyQuery } from "@apollo/client/react";
import {
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GET_INBOX_NOTES from "@graphql/queries/GetInboxNotes";
import { SEARCH_DEBOUNCE_TIME } from "@src/constants";
import { useEffect, useEffectEvent, useRef, useState } from "react";

import { useDebouncedValue } from "../../../hooks";
import NoteItem from "../../dashboard/dashboardInbox/NoteItem";

function GlobalSearchInput() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const LIMIT = 10;
  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    SEARCH_DEBOUNCE_TIME,
  );

  const [searchNotes, { data, loading }] = useLazyQuery(
    GET_INBOX_NOTES,
    {
      fetchPolicy: "no-cache",
    },
  );

  const searchNotesEvent = useEffectEvent(() => {
    searchNotes({
      variables: {
        filter: "all",
        limit: LIMIT,
        offset: currentOffset,
        searchQuery: debouncedSearchQuery,
      },
    });
  });

  useEffect(() => {
    if (debouncedSearchQuery.trim().length > 0) {
      searchNotesEvent();
    }
  }, [currentOffset, debouncedSearchQuery]);

  const handleClickOutside = (event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target) &&
      inputRef.current &&
      !inputRef.current.contains(event.target)
    ) {
      setIsOpen(false);
      setIsFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const nextValue = e.target.value;
    setCurrentOffset(0);
    setSearchQuery(nextValue);
    setIsOpen(nextValue.trim().length > 0);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    if (
      searchQuery.trim().length > 0 &&
      data?.inboxNotes?.notes?.length > 0
    ) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
  };

  const handleClear = () => {
    setSearchQuery("");
    setIsOpen(false);
  };

  const handleResultClick = () => {
    setIsOpen(false);
    setSearchQuery("");
    setIsFocused(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setIsFocused(false);
      inputRef.current?.blur();
    }
  };

  const handlePreviousPage = () => {
    const newOffset = Math.max(0, currentOffset - LIMIT);
    setCurrentOffset(newOffset);
  };

  const handleNextPage = () => {
    const newOffset = currentOffset + LIMIT;
    setCurrentOffset(newOffset);
  };

  const notes = data?.inboxNotes?.notes || [];
  const hasResults = notes.length > 0;
  const showDropdown =
    isOpen &&
    (hasResults ||
      loading ||
      (searchQuery.trim().length > 0 && !loading));

  return (
    <div className="relative w-full max-w-2xl">
      <div className="relative">
        <div className="absolute z-10 inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <FontAwesomeIcon
            icon={faSearch}
            className="text-gray-400"
          />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder="Search for any note..."
          className={`appearance-none relative block w-full px-3 py-2 border border-input bg-input placeholder-gray-400 text-primary rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-300 text-xs md:text-sm pl-10 pr-10 transition-all ${
            isFocused &&
            "ring-2 ring-indigo-300 shadow-lg shadow-primary"
          }`}
        />
        {searchQuery && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <button
              onClick={handleClear}
              className="text-secondary hover:text-primary transition-colors"
              type="button"
            >
              <FontAwesomeIcon icon={faTimes} className="text-sm" />
            </button>
          </div>
        )}
      </div>

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-primary border border-secondary rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto pt-2"
        >
          {loading && (
            <div className="p-4 text-center text-secondary">
              <FontAwesomeIcon
                icon={faSearch}
                className="animate-pulse mr-2"
              />
              Searching...
            </div>
          )}

          {!loading && hasResults && (
            <div className="pb-2">
              {notes.map((note) => (
                <div key={note.uuid} className="px-2 py-1">
                  <NoteItem
                    hideNoteStatus={true}
                    note={note}
                    dashboard={true}
                    handleActionWithPrevention={(fn) => {
                      handleResultClick();
                      fn();
                    }}
                  />
                </div>
              ))}
              {data?.inboxNotes?.totalNotes > 10 && (
                <div className="border-t border-secondary">
                  {data.inboxNotes.totalNotes > LIMIT && (
                    <div className="flex justify-between items-center px-3 pt-3 pb-1">
                      <button
                        onClick={handlePreviousPage}
                        disabled={currentOffset === 0}
                        className="flex items-center px-3 py-1 text-sm text-secondary hover:text-brand disabled:text-disabled disabled:cursor-not-allowed transition-colors"
                      >
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          className="mr-1"
                        />
                        Back
                      </button>
                      <span className="text-xs text-secondary">
                        Page {Math.floor(currentOffset / LIMIT) + 1}{" "}
                        of{" "}
                        {Math.ceil(
                          data.inboxNotes.totalNotes / LIMIT,
                        )}
                      </span>
                      <button
                        onClick={handleNextPage}
                        disabled={
                          currentOffset + LIMIT >=
                          data.inboxNotes.totalNotes
                        }
                        className="flex items-center px-3 py-1 text-sm text-secondary hover:text-brand disabled:text-disabled disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="ml-1"
                        />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {!loading &&
            !hasResults &&
            searchQuery.trim().length > 0 && (
              <div className="p-4 text-center text-secondary">
                No notes found for "{searchQuery}"
              </div>
            )}
        </div>
      )}
    </div>
  );
}

export default GlobalSearchInput;
